import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, TextField, Button, Alert, Paper,
  MenuItem, Chip, CircularProgress, Table, TableBody,
  TableCell, TableRow, TableHead, IconButton, Tooltip, Grid,
  Autocomplete, Avatar,
} from '@mui/material';
import AndroidIcon from '@mui/icons-material/Android';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import DevicesIcon from '@mui/icons-material/Devices';
import LanguageIcon from '@mui/icons-material/Language';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import api from '../../services/api';
import { adminUsersService, AdminClientUser } from '../../services/admin-users.service';
import { requestNotificationPermission, resetNotificationData } from '../../services/firebase-notification.service';
import { toastService } from '../../services/toast.service';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DeviceToken {
  id: number;
  token: string;
  platform: 'android' | 'ios' | 'web';
  lastActiveAt: string;
  updatedAt: string;
}

interface SendResult {
  sent: number;
  skipped: number;
}

type MobilePlatform = 'android' | 'ios' | 'all';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getErrorMessage = (error: any): string => {
  if (error.response?.data) {
    const data = error.response.data;
    if (data.message) {
      const msg = Array.isArray(data.message) ? data.message.join('\n') : data.message;
      return data.statusCode && data.error ? `${data.statusCode} ${data.error}: ${msg}` : msg;
    }
  }
  return error.message || 'Unknown error';
};

const relativeTime = (iso: string): string => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'щойно';
  if (mins < 60) return `${mins} хв тому`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} год тому`;
  return `${Math.floor(hours / 24)} дн тому`;
};

const PlatformChip: React.FC<{ platform: 'android' | 'ios' | 'web' }> = ({ platform }) => {
  const map = {
    android: { icon: <AndroidIcon />,    label: 'Android', color: 'success' as const },
    ios:     { icon: <PhoneIphoneIcon />, label: 'iOS',     color: 'default' as const },
    web:     { icon: <LanguageIcon />,    label: 'Web',     color: 'primary' as const },
  };
  const m = map[platform];
  return <Chip size="small" icon={m.icon} label={m.label} color={m.color} />;
};

// ─── Main component ───────────────────────────────────────────────────────────

export const DevNotificationTools: React.FC = () => {
  const STORAGE_KEY = 'dev-push-last-user';

  const restoreUser = (): AdminClientUser | null => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const savedUser = restoreUser();

  // Shared user
  const [email, setEmail] = useState(savedUser?.email ?? '');
  const [selectedUser, setSelectedUser] = useState<AdminClientUser | null>(savedUser);
  const [userOptions, setUserOptions] = useState<AdminClientUser[]>(savedUser ? [savedUser] : []);
  const [userSearch, setUserSearch] = useState(savedUser ? `${savedUser.name} (${savedUser.email})` : '');
  const [usersLoading, setUsersLoading] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstSearch = useRef(!!savedUser);

  // Tokens section
  const [tokens, setTokens] = useState<DeviceToken[]>([]);
  const [tokensLoading, setTokensLoading] = useState(false);
  const [tokensLoaded, setTokensLoaded] = useState(false);

  // Web push section
  const [webTitle, setWebTitle] = useState('Марія Іваненко');
  const [webBody, setWebBody] = useState('Привіт! Ти бачив оновлення дизайну?');
  const [webChatId, setWebChatId] = useState('34');
  const [permissionStatus, setPermissionStatus] = useState<string>(Notification.permission);

  // Firebase health check
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthResult, setHealthResult] = useState<{ status: string; message: string; dryRunResult?: string; error?: string } | null>(null);

  // Mobile push section
  const [mobileTitle, setMobileTitle] = useState('Тестове повідомлення');
  const [mobileBody, setMobileBody] = useState('Це тестовий push для мобільного додатку');
  const [mobilePlatform, setMobilePlatform] = useState<MobilePlatform>('all');
  const [mobileSending, setMobileSending] = useState(false);
  const [sendResult, setSendResult] = useState<SendResult | null>(null);

  // ─── User search ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isFirstSearch.current) {
      isFirstSearch.current = false;
      return;
    }
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      setUsersLoading(true);
      try {
        const res = await adminUsersService.getClients({ search: userSearch, limit: 25 });
        setUserOptions(res.data);
      } catch {
        // silently ignore
      } finally {
        setUsersLoading(false);
      }
    }, 300);
  }, [userSearch]);

  // ─── Token handlers ──────────────────────────────────────────────────────────
  const handleLoadTokens = async () => {
    if (!email) { toastService.error('Виберіть користувача'); return; }
    setTokensLoading(true);
    setTokens([]);
    try {
      const res = await api.get(`/developer/device-tokens/${encodeURIComponent(email)}`);
      setTokens(res.data?.data ?? []);
      setTokensLoaded(true);
    } catch (error) {
      toastService.error(getErrorMessage(error));
    } finally {
      setTokensLoading(false);
    }
  };

  const handleCopyToken = (token: string) => {
    navigator.clipboard.writeText(token);
    toastService.success('Токен скопійовано');
  };

  // ─── Web push handlers ───────────────────────────────────────────────────────
  const handleCheckPermission = async () => {
    try {
      const token = await requestNotificationPermission();
      setPermissionStatus(Notification.permission);
      if (token) toastService.success('Permission granted! Token received.');
      else toastService.warn('Permission granted but no token received.');
    } catch {
      setPermissionStatus(Notification.permission);
      toastService.error('Failed to get permission.');
    }
  };

  const handleSimplePush = () => {
    if (!email) { toastService.error('Введіть email'); return; }
    api.post('/notifications/test-push-simple', { email, title: webTitle, body: webBody })
      .then(() => toastService.success('Simple Push надіслано!'))
      .catch(e => toastService.error(getErrorMessage(e)));
  };

  const handleChatPush = () => {
    if (!email) { toastService.error('Введіть email'); return; }
    api.post('/notifications/test-chat-push', { email, senderName: webTitle, messageText: webBody, chatId: webChatId })
      .then(() => toastService.success('Chat Push надіслано!'))
      .catch(e => toastService.error(getErrorMessage(e)));
  };

  const handleResetLocal = async () => {
    try {
      await resetNotificationData();
      toastService.success('Local push скинуто. Перезавантажте сторінку.');
    } catch (e: any) {
      toastService.error(e.message || 'Помилка скидання');
    }
  };

  // ─── Firebase health check ───────────────────────────────────────────────────
  const handleFirebaseHealth = async () => {
    setHealthLoading(true);
    setHealthResult(null);
    try {
      const res = await api.get('/developer/firebase-health');
      setHealthResult(res.data?.data ?? res.data);
    } catch (error: any) {
      setHealthResult(error.response?.data?.data ?? error.response?.data ?? { status: 'error', message: getErrorMessage(error) });
    } finally {
      setHealthLoading(false);
    }
  };

  // ─── Mobile push handlers ────────────────────────────────────────────────────
  const handleMobilePush = async (overridePlatform?: 'android' | 'ios') => {
    if (!email) { toastService.error('Введіть email'); return; }
    setSendResult(null);
    setMobileSending(true);
    try {
      const platform = overridePlatform ?? (mobilePlatform === 'all' ? undefined : mobilePlatform);
      const res = await api.post('/developer/test-mobile-push', {
        email,
        title: mobileTitle || undefined,
        body: mobileBody || undefined,
        ...(platform ? { platform } : {}),
      });
      const result: SendResult = res.data?.data ?? { sent: 0, skipped: 0 };
      setSendResult(result);
    } catch (error) {
      toastService.error(getErrorMessage(error));
    } finally {
      setMobileSending(false);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────────
  const androidCount = tokens.filter(t => t.platform === 'android').length;
  const iosCount     = tokens.filter(t => t.platform === 'ios').length;
  const webCount     = tokens.filter(t => t.platform === 'web').length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

      {/* ── Firebase health check ─────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HealthAndSafetyIcon fontSize="small" /> Firebase Health Check
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Перевіряє зв'язок між сервером і Firebase Admin SDK (dry-run відправка).
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="info"
            onClick={handleFirebaseHealth}
            disabled={healthLoading}
            startIcon={healthLoading ? <CircularProgress size={16} color="inherit" /> : <HealthAndSafetyIcon />}
          >
            {healthLoading ? 'Перевірка...' : 'Перевірити'}
          </Button>

          {healthResult && (
            <Alert
              severity={healthResult.status === 'ok' ? 'success' : 'error'}
              sx={{ flex: 1 }}
            >
              <strong>{healthResult.message}</strong>
              {healthResult.dryRunResult && (
                <Typography variant="caption" display="block" sx={{ mt: 0.5, fontFamily: 'monospace' }}>
                  {healthResult.dryRunResult}
                </Typography>
              )}
              {healthResult.error && (
                <Typography variant="caption" display="block" sx={{ mt: 0.5, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {healthResult.error}
                </Typography>
              )}
            </Alert>
          )}
        </Box>
      </Paper>

      {/* ── Shared user selector ───────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <DevicesIcon color="action" />
        <Autocomplete
          sx={{ flex: 1, minWidth: 280, maxWidth: 480 }}
          options={userOptions}
          loading={usersLoading}
          value={selectedUser}
          inputValue={userSearch}
          onInputChange={(_, v) => setUserSearch(v)}
          onChange={(_, user) => {
            setSelectedUser(user);
            setEmail(user?.email ?? '');
            setTokensLoaded(false);
            setSendResult(null);
            if (user) {
              localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
            } else {
              localStorage.removeItem(STORAGE_KEY);
            }
          }}
          getOptionLabel={(u) => `${u.name} (${u.email})`}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          filterOptions={(x) => x}
          noOptionsText="Нікого не знайдено"
          renderOption={(props, u) => (
            <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar src={u.avatar} sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
                {u.name[0]}
              </Avatar>
              <Box>
                <Typography variant="body2" fontWeight={500}>{u.name}</Typography>
                <Typography variant="caption" color="text.secondary">{u.email}</Typography>
              </Box>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Користувач (для тестів)"
              placeholder="Пошук за іменем або email..."
              InputProps={{
                ...params.InputProps,
                startAdornment: selectedUser?.avatar ? (
                  <Avatar src={selectedUser.avatar} sx={{ width: 24, height: 24, mr: 0.5 }} />
                ) : params.InputProps.startAdornment,
                endAdornment: (
                  <>
                    {usersLoading && <CircularProgress size={16} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        {email && (
          <Chip
            label={email}
            size="small"
            color="primary"
            variant="outlined"
            onDelete={() => { setSelectedUser(null); setEmail(''); setUserSearch(''); setTokensLoaded(false); setSendResult(null); localStorage.removeItem(STORAGE_KEY); }}
          />
        )}
      </Paper>

      {/* ── Device tokens ──────────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Зареєстровані пристрої користувача
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleLoadTokens}
            disabled={tokensLoading || !email}
            startIcon={tokensLoading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
          >
            {tokensLoading ? 'Завантаження...' : 'Завантажити'}
          </Button>

          {tokensLoaded && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {webCount > 0     && <Chip size="small" icon={<LanguageIcon />}    label={`Web: ${webCount}`}         color="primary" />}
              {androidCount > 0 && <Chip size="small" icon={<AndroidIcon />}     label={`Android: ${androidCount}`} color="success" />}
              {iosCount > 0     && <Chip size="small" icon={<PhoneIphoneIcon />} label={`iOS: ${iosCount}`} />}
              {tokens.length === 0 && <Chip size="small" label="Токенів не знайдено" color="warning" />}
            </Box>
          )}
        </Box>

        {tokens.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Platform</TableCell>
                <TableCell>Token</TableCell>
                <TableCell>Last active</TableCell>
                <TableCell>Оновлено</TableCell>
                <TableCell padding="none" />
              </TableRow>
            </TableHead>
            <TableBody>
              {tokens.map((dt) => (
                <TableRow key={dt.id}>
                  <TableCell><PlatformChip platform={dt.platform} /></TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.7rem', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {dt.token}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={new Date(dt.lastActiveAt).toLocaleString('uk-UA')}>
                      <Typography variant="caption" color="text.secondary">{relativeTime(dt.lastActiveAt)}</Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={new Date(dt.updatedAt).toLocaleString('uk-UA')}>
                      <Typography variant="caption" color="text.secondary">{relativeTime(dt.updatedAt)}</Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell padding="none">
                    <Tooltip title="Скопіювати токен">
                      <IconButton size="small" onClick={() => handleCopyToken(dt.token)}>
                        <ContentCopyIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* ── Web push + Mobile push ─────────────────────────────────────────── */}
      <Grid container spacing={2}>

        {/* Web push */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LanguageIcon fontSize="small" /> Web Push (Firebase)
            </Typography>

            <Alert
              severity={permissionStatus === 'granted' ? 'success' : 'warning'}
              action={
                <Button size="small" onClick={handleCheckPermission}>
                  Оновити токен
                </Button>
              }
              sx={{ mb: 2 }}
            >
              Permission: <strong>{permissionStatus}</strong>
            </Alert>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <TextField label="Title / Sender Name" value={webTitle} onChange={(e) => setWebTitle(e.target.value)} fullWidth helperText="Для Chat Push — ім'я відправника" />
              <TextField label="Body / Message Text" value={webBody} onChange={(e) => setWebBody(e.target.value)} fullWidth multiline rows={2} helperText="Для Chat Push — текст повідомлення" />
              <TextField label="Chat ID" value={webChatId} onChange={(e) => setWebChatId(e.target.value)} fullWidth helperText="Тільки для Chat Push (редірект)" />

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', pt: 0.5 }}>
                <Button variant="contained" onClick={handleSimplePush}>Простий Push</Button>
                <Button variant="contained" color="secondary" onClick={handleChatPush}>Chat Push</Button>
                <Button variant="outlined" color="warning" onClick={handleResetLocal}>Скинути Local</Button>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Mobile push */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DevicesIcon fontSize="small" /> Mobile Push (FCM)
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <TextField label="Title" value={mobileTitle} onChange={(e) => setMobileTitle(e.target.value)} fullWidth />
              <TextField label="Body" value={mobileBody} onChange={(e) => setMobileBody(e.target.value)} fullWidth multiline rows={2} />

              <TextField
                select
                label="Platform"
                value={mobilePlatform}
                onChange={(e) => { setMobilePlatform(e.target.value as MobilePlatform); setSendResult(null); }}
                fullWidth
              >
                <MenuItem value="all">Всі (android + ios)</MenuItem>
                <MenuItem value="android">Android</MenuItem>
                <MenuItem value="ios">iOS</MenuItem>
              </TextField>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', pt: 0.5 }}>
                <Button
                  variant="contained"
                  onClick={() => handleMobilePush()}
                  disabled={mobileSending}
                  startIcon={mobileSending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                >
                  {mobileSending ? 'Надсилаємо...' : 'Надіслати'}
                </Button>

                {tokensLoaded && androidCount > 0 && (
                  <Button variant="outlined" color="success" size="small" startIcon={<AndroidIcon />} onClick={() => handleMobilePush('android')} disabled={mobileSending}>
                    Android ({androidCount})
                  </Button>
                )}
                {tokensLoaded && iosCount > 0 && (
                  <Button variant="outlined" size="small" startIcon={<PhoneIphoneIcon />} onClick={() => handleMobilePush('ios')} disabled={mobileSending}>
                    iOS ({iosCount})
                  </Button>
                )}
              </Box>

              {sendResult && (
                <Alert severity={sendResult.sent > 0 ? 'success' : 'warning'}>
                  Надіслано: <strong>{sendResult.sent}</strong> &nbsp;·&nbsp; Пропущено: <strong>{sendResult.skipped}</strong>
                </Alert>
              )}
            </Box>
          </Paper>
        </Grid>

      </Grid>
    </Box>
  );
};
